import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, ChevronLeft, Save, Film, GripVertical, Rocket, PauseCircle, CheckSquare, Square } from "lucide-react";
import { mediaUrl } from "../lib/media";
import FileUpload from "./FileUpload";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY_SERIES = {
    title: "", genre: "", rating: 4.5, description: "",
    poster: "", backdrop: "", trailer_url: "", tags: [], episodes: [],
};
const EMPTY_EPISODE = {
    title: "", part_number: 1, duration: "45 min", released_at: "",
    video_url: "", thumbnail: "", description: "", is_coming_soon: false, release_date: "",
};

export default function SeriesEditor() {
    const [list, setList] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState(new Set());

    const load = async () => {
        const r = await api.get("/series");
        setList(r.data);
    };
    useEffect(() => { load(); }, []);

    const startNew = () => {
        setEditing({ ...EMPTY_SERIES, tags: [], episodes: [] });
        setCreating(true); setSelected(new Set());
    };
    const startEdit = (s) => {
        // give any episode without id a temporary one so DnD works
        const eps = (s.episodes || []).map((e) => ({ ...e, id: e.id || `tmp-${Math.random().toString(36).slice(2)}` }));
        setEditing({ ...JSON.parse(JSON.stringify(s)), episodes: eps });
        setCreating(false); setSelected(new Set());
    };
    const cancel = () => { setEditing(null); setCreating(false); setSelected(new Set()); };

    const save = async () => {
        if (!editing.title || !editing.genre || !editing.poster) {
            toast.error("Title, genre, and poster are required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...editing,
                rating: Number(editing.rating) || 0,
                trailer_url: editing.trailer_url || null,
                episodes: (editing.episodes || []).map((ep, i) => ({
                    ...ep,
                    part_number: Number(ep.part_number) || i + 1,
                    is_coming_soon: !!ep.is_coming_soon,
                    video_url: ep.video_url || null,
                    released_at: ep.released_at || null,
                    release_date: ep.release_date || null,
                })),
            };
            if (creating) {
                delete payload.id; delete payload.created_at;
                await api.post("/admin/series", payload);
                toast.success("Series created.");
            } else {
                await api.put(`/admin/series/${editing.id}`, payload);
                toast.success("Series saved.");
            }
            await load();
            setEditing(null); setCreating(false); setSelected(new Set());
        } catch (err) {
            const msg = err?.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this series and all its episodes?")) return;
        try {
            await api.delete(`/admin/series/${id}`);
            toast.success("Series deleted.");
            await load();
        } catch { toast.error("Delete failed."); }
    };

    /* -------- Episode helpers -------- */
    const addEpisode = () => {
        const eps = editing.episodes || [];
        const next = eps.length ? Math.max(...eps.map(e => Number(e.part_number) || 0)) + 1 : 1;
        setEditing({
            ...editing,
            episodes: [...eps, { ...EMPTY_EPISODE, id: `tmp-${Math.random().toString(36).slice(2)}`, part_number: next }],
        });
    };
    const updateEpisode = (id, patch) => {
        const eps = editing.episodes.map((e) => e.id === id ? { ...e, ...patch } : e);
        setEditing({ ...editing, episodes: eps });
    };
    const removeEpisode = (id) => {
        setEditing({ ...editing, episodes: editing.episodes.filter(e => e.id !== id) });
        const next = new Set(selected); next.delete(id); setSelected(next);
    };

    /* -------- Drag & drop -------- */
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = editing.episodes.findIndex(e => e.id === active.id);
        const newIndex = editing.episodes.findIndex(e => e.id === over.id);
        const reordered = arrayMove(editing.episodes, oldIndex, newIndex).map((e, i) => ({ ...e, part_number: i + 1 }));
        setEditing({ ...editing, episodes: reordered });
    };

    /* -------- Bulk publish -------- */
    const toggleSelect = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const selectAll = () => setSelected(new Set(editing.episodes.map(e => e.id)));
    const clearSel = () => setSelected(new Set());
    const applyBulk = (comingSoon) => {
        if (selected.size === 0) { toast.error("Select at least one episode."); return; }
        const eps = editing.episodes.map((e) => selected.has(e.id) ? { ...e, is_coming_soon: comingSoon, released_at: !comingSoon && !e.released_at ? new Date().toISOString().slice(0, 10) : e.released_at } : e);
        setEditing({ ...editing, episodes: eps });
        toast.success(`Marked ${selected.size} episode(s) as ${comingSoon ? "Coming Soon" : "Published"}. Click Save Changes.`);
    };

    /* -------- Views -------- */
    if (editing) {
        return (
            <div data-testid="series-editor" className="grid grid-cols-1 gap-6">
                <div className="flex items-center justify-between">
                    <button onClick={cancel} data-testid="editor-back-btn"
                        className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-[#FFD400] transition-colors">
                        <ChevronLeft className="w-4 h-4" /> Back to list
                    </button>
                    <div className="flex gap-2">
                        <button onClick={cancel}
                            className="inline-flex items-center gap-2 text-sm bg-white/5 border border-white/10 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
                            <X className="w-4 h-4" /> Cancel
                        </button>
                        <button onClick={save} disabled={saving} data-testid="editor-save-btn"
                            className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-5 py-2 hover:scale-105 transition-transform disabled:opacity-60 disabled:hover:scale-100">
                            <Save className="w-4 h-4" /> {saving ? "Saving…" : creating ? "Create Series" : "Save Changes"}
                        </button>
                    </div>
                </div>

                {/* Series fields */}
                <div className="rounded-[18px] border border-white/10 bg-white/[0.02] p-6">
                    <h3 className="font-display font-bold text-xl mb-4">
                        {creating ? "New Series" : `Editing: ${editing.title || "—"}`}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="series-title" />
                        <TextField label="Genre *" value={editing.genre} onChange={(v) => setEditing({ ...editing, genre: v })} placeholder="Crime Thriller" testId="series-genre" />
                        <TextField label="Rating (0-5)" value={editing.rating} onChange={(v) => setEditing({ ...editing, rating: v })} testId="series-rating" />
                        <TextField label="Tags (comma separated)" value={(editing.tags || []).join(", ")}
                            onChange={(v) => setEditing({ ...editing, tags: v.split(",").map(x => x.trim()).filter(Boolean) })}
                            placeholder="Crime, Thriller, AI Original" testId="series-tags" />
                        <FileUpload label="Poster *" kind="poster" accept="image/*" testId="series-poster"
                            value={editing.poster} onChange={(v) => setEditing({ ...editing, poster: v })} />
                        <FileUpload label="Backdrop" kind="poster" accept="image/*" testId="series-backdrop"
                            value={editing.backdrop} onChange={(v) => setEditing({ ...editing, backdrop: v })} />
                        <div className="md:col-span-2">
                            <FileUpload label="Trailer (MP4)" kind="video" accept="video/*" testId="series-trailer"
                                value={editing.trailer_url} onChange={(v) => setEditing({ ...editing, trailer_url: v })} />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="text-xs uppercase tracking-widest text-white/50">Description</label>
                        <textarea data-testid="series-description" rows={4}
                            value={editing.description}
                            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                            className="mt-1 w-full bg-white/5 border border-white/10 rounded-[12px] px-3 py-2 text-white focus:border-[#FFD400] outline-none resize-none transition-colors" />
                    </div>
                </div>

                {/* Episodes with DnD + bulk toolbar */}
                <div className="rounded-[18px] border border-white/10 bg-white/[0.02] p-6">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <h3 className="font-display font-bold text-xl">Episodes ({(editing.episodes || []).length})</h3>
                        <button onClick={addEpisode} data-testid="episode-add-btn"
                            className="inline-flex items-center gap-2 text-sm bg-[#FFD400] text-black font-bold rounded-full px-4 py-2 hover:scale-105 transition-transform">
                            <Plus className="w-4 h-4" /> Add Episode
                        </button>
                    </div>

                    {/* Bulk toolbar */}
                    {editing.episodes.length > 0 && (
                        <div data-testid="bulk-toolbar" className="mb-4 flex items-center gap-2 flex-wrap p-3 rounded-[12px] bg-white/[0.03] border border-white/10">
                            <button onClick={selected.size === editing.episodes.length ? clearSel : selectAll}
                                data-testid="bulk-select-all"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 border border-white/10 text-white rounded-full px-3 py-1.5 hover:bg-white/20 transition-colors">
                                {selected.size === editing.episodes.length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                                {selected.size === editing.episodes.length ? "Clear" : "Select all"}
                            </button>
                            <span className="text-xs text-white/50">{selected.size} selected</span>
                            <div className="flex-1" />
                            <button onClick={() => applyBulk(false)} data-testid="bulk-publish"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-400/90 text-black rounded-full px-3 py-1.5 hover:scale-105 transition-transform">
                                <Rocket className="w-3.5 h-3.5" /> Publish
                            </button>
                            <button onClick={() => applyBulk(true)} data-testid="bulk-unpublish"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 border border-white/10 text-white rounded-full px-3 py-1.5 hover:bg-white/20 transition-colors">
                                <PauseCircle className="w-3.5 h-3.5" /> Mark Coming Soon
                            </button>
                        </div>
                    )}

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={editing.episodes.map(e => e.id)} strategy={verticalListSortingStrategy}>
                            <div className="space-y-4">
                                {editing.episodes.map((ep, idx) => (
                                    <SortableEpisode
                                        key={ep.id}
                                        ep={ep}
                                        idx={idx}
                                        selected={selected.has(ep.id)}
                                        onToggle={() => toggleSelect(ep.id)}
                                        onChange={(patch) => updateEpisode(ep.id, patch)}
                                        onRemove={() => removeEpisode(ep.id)}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>

                    {editing.episodes.length === 0 && (
                        <p className="text-sm text-white/50">No episodes yet. Add one to get started.</p>
                    )}
                </div>
            </div>
        );
    }

    /* -------- List view -------- */
    return (
        <div data-testid="series-list-admin" className="grid grid-cols-1 gap-6">
            <div className="rounded-[18px] border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display font-bold text-xl inline-flex items-center gap-2">
                        <Film className="w-5 h-5 text-[#FFD400]" /> Series ({list.length})
                    </h2>
                    <button onClick={startNew} data-testid="series-new-btn"
                        className="inline-flex items-center gap-2 bg-[#FFD400] text-black font-bold rounded-full px-5 py-2.5 hover:scale-105 transition-transform">
                        <Plus className="w-4 h-4" /> New Series
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {list.map((s) => (
                        <div key={s.id} data-testid={`series-admin-${s.id}`} className="relative rounded-[14px] overflow-hidden border border-white/10 bg-white/[0.03] group">
                            <div className="aspect-video relative">
                                <img src={mediaUrl(s.backdrop || s.poster)} alt={s.title} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                            </div>
                            <div className="p-4 -mt-14 relative">
                                <h3 className="font-display font-bold text-lg text-white">{s.title}</h3>
                                <div className="text-xs text-white/60 mt-1 flex items-center gap-2">
                                    <span>{s.genre}</span>
                                    <span className="w-1 h-1 rounded-full bg-white/40" />
                                    <span>{(s.episodes || []).length} eps</span>
                                </div>
                                <div className="mt-4 flex gap-2">
                                    <button onClick={() => startEdit(s)} data-testid={`series-edit-${s.id}`}
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-white/10 border border-white/10 text-white rounded-full py-2 hover:bg-white/20 transition-colors">
                                        <Edit3 className="w-3.5 h-3.5" /> Edit
                                    </button>
                                    <button onClick={() => remove(s.id)} data-testid={`series-delete-${s.id}`}
                                        className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-white/5 border border-white/10 text-white/70 rounded-full px-3 py-2 hover:bg-red-500/20 hover:text-red-300 transition-colors">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {list.length === 0 && (
                        <p className="col-span-full text-sm text-white/50">No series yet. Click "New Series" to add one.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

/* -------- Sortable episode card -------- */
function SortableEpisode({ ep, idx, selected, onToggle, onChange, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ep.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} data-testid={`episode-editor-${idx}`}
            className={`rounded-[14px] border ${selected ? "border-[#FFD400]/50 bg-[#FFD400]/[0.03]" : "border-white/10 bg-white/[0.03]"} p-4`}>
            <div className="flex items-center gap-3 mb-3">
                <button {...attributes} {...listeners} type="button" aria-label="Drag"
                    data-testid={`ep-drag-${idx}`}
                    className="p-1 rounded cursor-grab active:cursor-grabbing text-white/50 hover:text-[#FFD400] transition-colors">
                    <GripVertical className="w-5 h-5" />
                </button>
                <button type="button" onClick={onToggle} data-testid={`ep-select-${idx}`}
                    className="p-1 text-white/70 hover:text-[#FFD400] transition-colors">
                    {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>
                <span className="text-[11px] uppercase tracking-widest text-[#FFD400] font-bold">
                    Part {ep.part_number || idx + 1}
                </span>
                {ep.is_coming_soon && (
                    <span className="text-[10px] uppercase font-bold tracking-widest bg-white/10 text-white/70 rounded px-1.5 py-0.5">Coming soon</span>
                )}
                <div className="flex-1" />
                <button type="button" onClick={onRemove} data-testid={`episode-delete-${idx}`}
                    className="p-1.5 text-white/60 hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextField label="Title" value={ep.title} onChange={(v) => onChange({ title: v })} testId={`ep-title-${idx}`} />
                <TextField label="Part number" value={ep.part_number} onChange={(v) => onChange({ part_number: v })} testId={`ep-part-${idx}`} />
                <TextField label="Duration" value={ep.duration} onChange={(v) => onChange({ duration: v })} placeholder="45 min" testId={`ep-duration-${idx}`} />
                <TextField label="Released at (YYYY-MM-DD)" value={ep.released_at || ""} onChange={(v) => onChange({ released_at: v })} testId={`ep-released-${idx}`} />
                <FileUpload label="Thumbnail" kind="thumbnail" accept="image/*" testId={`ep-thumb-${idx}`}
                    value={ep.thumbnail} onChange={(v) => onChange({ thumbnail: v })} />
                <FileUpload label="Video (MP4)" kind="video" accept="video/*" testId={`ep-video-${idx}`}
                    value={ep.video_url} onChange={(v) => onChange({ video_url: v })} />
            </div>
            <div className="mt-3">
                <label className="text-xs uppercase tracking-widest text-white/50">Description</label>
                <textarea data-testid={`ep-desc-${idx}`} rows={2} value={ep.description || ""}
                    onChange={(e) => onChange({ description: e.target.value })}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-[12px] px-3 py-2 text-white focus:border-[#FFD400] outline-none resize-none transition-colors" />
            </div>
            <div className="mt-3 flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm text-white/70">
                    <input type="checkbox" checked={!!ep.is_coming_soon}
                        onChange={(e) => onChange({ is_coming_soon: e.target.checked })}
                        data-testid={`ep-coming-${idx}`} />
                    Coming Soon
                </label>
                {ep.is_coming_soon && (
                    <div className="flex-1 min-w-[240px]">
                        <TextField label="Release date (ISO)" value={ep.release_date || ""}
                            onChange={(v) => onChange({ release_date: v })}
                            placeholder="2026-06-01T00:00:00Z" testId={`ep-release-${idx}`} />
                    </div>
                )}
            </div>
        </div>
    );
}

function TextField({ label, value, onChange, placeholder, testId }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-white/50">{label}</label>
            <input
                data-testid={testId}
                value={value == null ? "" : value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-[12px] px-3 py-2 text-white focus:border-[#FFD400] outline-none transition-colors"
            />
        </div>
    );
}
